import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr } from "../lib/api";
import { inputCls, btnPrimary } from "../components/bits";
import { HeartHandshake, ShieldCheck, Users, ClipboardCheck } from "lucide-react";

function AuthShell({ children, title, sub }) {
  return (
    <div className="min-h-screen bg-bg flex">
      <div className="hidden lg:block w-[42%] relative overflow-hidden">
        <img src="/assets/hero-ithra.jpg" alt="إثراء" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-turq-900/95 via-turq-900/60 to-transparent" />
        <div className="absolute bottom-0 right-0 p-10 text-white">
          <div className="font-head font-black text-3xl mb-2">سجل عطاء وأثر تعليم الشرقية</div>
          <div className="text-turq-300 font-head font-bold">«نرصد العطاء … ونقيس الأثر»</div>
          <p className="text-white/70 text-sm mt-3 max-w-sm leading-relaxed">
            قسم المسؤولية المجتمعية والعمل التطوعي — الإدارة العامة للتعليم بالمنطقة الشرقية
          </p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-turq-900 flex items-center justify-center">
              <HeartHandshake className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="font-head font-black text-lg text-turq-900 dark:text-turq-300">عطاء</div>
              <div className="text-[11px] text-mutext">تعليم الشرقية</div>
            </div>
          </div>
          <h1 className="font-head font-black text-2xl mb-1">{title}</h1>
          <p className="text-sm text-mutext mb-6">{sub}</p>
          {children}
          <div className="text-[10px] text-mutext text-center mt-10 pt-4 border-t border-brd/60">
            قسم المسؤولية المجتمعية والعمل التطوعي — الإدارة العامة للتعليم بالمنطقة الشرقية
          </div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { setUser, user } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("coordinator");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav(user.role === "admin" ? "/admin" : user.role === "supervisor" ? "/sup" : "/portal", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data: u } = await api.post("/auth/login", { email, password });
      setUser(u);
      nav(u.role === "admin" ? "/admin" : u.role === "supervisor" ? "/sup" : "/portal");
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setBusy(false);
    }
  };

  const roleCard = (key, label, desc, Icon, tid) => (
    <button type="button" key={key} data-testid={tid} onClick={() => setRole(key)}
      className={`rounded-2xl border-2 p-4 text-center transition-all ${
        role === key
          ? "border-turq-600 bg-turq-50 dark:bg-surface2 text-turq-900 dark:text-turq-300 shadow-lg shadow-turq-600/15"
          : "border-brd bg-surface text-mutext hover:border-turq-300"}`}>
      <Icon className="w-6 h-6 mx-auto mb-2" />
      <div className="font-head font-bold text-sm">{label}</div>
      <div className="text-[10px] opacity-75 mt-0.5">{desc}</div>
    </button>
  );

  return (
    <AuthShell title="سجل عطاء وأثر تعليم الشرقية" sub="بصمتك في الخير تبقى — اختر بوابتك وسجّل دخولك">
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="role-selector">
        {roleCard("admin", "المدير العام", "المتابعة والإحصائيات المركزية", ShieldCheck, "role-admin-card")}
        {roleCard("supervisor", "فريق المتابعة", "الإشراف على قطاعات المدارس", ClipboardCheck, "role-supervisor-card")}
        {roleCard("coordinator", "منسق التطوع", "إدارة أنشطة وبيانات المدرسة", Users, "role-coordinator-card")}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {err && <div data-testid="login-error" className="bg-coral-100 text-coral-900 text-sm rounded-xl px-4 py-3">{err}</div>}
        <div>
          <label className="text-xs font-bold text-mutext block mb-1.5">البريد الإلكتروني أو الجوال أو اسم المستخدم</label>
          <input data-testid="login-email-input" type="text" required value={email}
            onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="example@edu.sa أو 05xxxxxxxx أو gwez1234" dir="ltr" />
        </div>
        <div>
          <label className="text-xs font-bold text-mutext block mb-1.5">كلمة المرور</label>
          <input data-testid="login-password-input" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" dir="ltr" />
          {role !== "admin" && (
            <p data-testid="login-default-pw-hint" className="text-[11px] text-turq-900 dark:text-turq-300 bg-turq-100/60 dark:bg-surface2 rounded-lg px-3 py-2 mt-1.5 leading-relaxed">
              إن لم تغيّر كلمة المرور من قبل: كلمة مرورك هي <b>رقم جوالك المسجل</b> (مثال: 05xxxxxxxx)
            </p>
          )}
        </div>
        <button data-testid="login-submit-btn" disabled={busy} className={`${btnPrimary} w-full justify-center`}>
          {busy ? "جارٍ الدخول …" : role === "admin" ? "دخول بوابة المدير العام" : role === "supervisor" ? "دخول بوابة فريق المتابعة" : "دخول بوابة منسق التطوع"}
        </button>
        <div className="flex items-center justify-between text-xs">
          <Link data-testid="forgot-password-link" to="/forgot-password" className="text-turq-600 hover:text-turq-900 font-bold">
            نسيت كلمة المرور؟
          </Link>
          <Link data-testid="back-to-landing-link" to="/" className="text-mutext hover:text-ink">
            العودة للمعرض العام
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
    } catch {
      /* الرد العام مقصود أمنياً (لا يكشف وجود البريد) */
    }
    setDone(true);
    setBusy(false);
  };

  return (
    <AuthShell title="استعادة كلمة المرور" sub="أدخل بريدك وسنرسل لك رابط إعادة التعيين">
      {done ? (
        <div data-testid="forgot-success" className="bg-turq-100 dark:bg-surface2 text-turq-900 dark:text-turq-300 text-sm rounded-xl px-4 py-4 leading-relaxed">
          إذا كان البريد مسجلاً لدينا، فقد أرسلنا إليه رابط إعادة التعيين. تحقق من بريدك خلال ساعة.
          <div className="mt-4">
            <Link data-testid="back-to-login-link" to="/login" className="font-bold underline">العودة لتسجيل الدخول</Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">البريد الإلكتروني</label>
            <input data-testid="forgot-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} className={inputCls} dir="ltr" placeholder="name@ataa.edu.sa" />
          </div>
          <button data-testid="forgot-submit-btn" disabled={busy} className={`${btnPrimary} w-full justify-center`}>
            {busy ? "جارٍ الإرسال …" : "إرسال رابط الاستعادة"}
          </button>
          <Link data-testid="back-to-login-link" to="/login" className="block text-center text-xs text-turq-600 font-bold">
            العودة لتسجيل الدخول
          </Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const token = params.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/reset-password", { token, password });
      nav("/login");
    } catch (ex) {
      setErr(fmtErr(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="كلمة مرور جديدة" sub="اختر كلمة مرور قوية (8 أحرف على الأقل)">
      {!token ? (
        <div data-testid="reset-invalid" className="bg-coral-100 text-coral-900 text-sm rounded-xl px-4 py-3">
          الرابط غير صالح. <Link to="/forgot-password" className="font-bold underline">اطلب رابطاً جديداً</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          {err && <div data-testid="reset-error" className="bg-coral-100 text-coral-900 text-sm rounded-xl px-4 py-3">{err}</div>}
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">كلمة المرور الجديدة</label>
            <input data-testid="reset-password-input" type="password" required minLength={8} value={password}
              onChange={(e) => setPassword(e.target.value)} className={inputCls} dir="ltr" placeholder="••••••••" />
          </div>
          <button data-testid="reset-submit-btn" disabled={busy} className={`${btnPrimary} w-full justify-center`}>
            {busy ? "جارٍ الحفظ …" : "تعيين كلمة المرور"}
          </button>
          <Link data-testid="back-to-login-link" to="/login" className="block text-center text-xs text-turq-600 font-bold">
            العودة لتسجيل الدخول
          </Link>
        </form>
      )}
    </AuthShell>
  );
}
